import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  ClockIcon, 
  UserGroupIcon, 
  TrophyIcon,
  HeartIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface SportContent {
  [key: string]: {
    title: string;
    subtitle: string;
    heroImage: string;
    description: string;
    longDescription: string[];
    benefits: string[];
    requirements: string[];
    training: {
      duration: string;
      groupSize: string;
      level: string;
    };
    equipment: string[];
    targetGroup: string;
  };
}

const SportDetail: React.FC = () => {
  const { sport } = useParams<{ sport: string }>();

  const sportContent: SportContent = {
    agility: {
      title: "Agility",
      subtitle: "Action und Teamarbeit auf höchstem Niveau",
      heroImage: "/images/sports/agility/agility-course.jpg",
      description: "Agility ist ein temporeicher und spannender Hundesport, bei dem es auf das perfekte Zusammenspiel zwischen Hund und Hundeführer ankommt.",
      longDescription: [
        "Der Hund durchläuft einen Parcours mit verschiedenen Hindernissen und zeigt dabei Geschicklichkeit, Schnelligkeit und Aufmerksamkeit. Agility ist ideal für Hunde und Halter, die Spaß an Bewegung haben und als Team neue Herausforderungen meistern möchten.",
        "Beim Agility führt der Hund auf Anweisung des Hundeführers einen abwechslungsreichen Parcours durch – mit Hindernissen wie Hürden, Tunneln, Slalom und Wippen. Dabei kommt es auf Geschwindigkeit und Präzision an: Der Hund muss die Hindernisse in der richtigen Reihenfolge und möglichst fehlerfrei bewältigen.",
        "Das Agility-Training beginnt mit den Grundlagen: Die Hunde lernen, Hindernisse sicher und konzentriert zu meistern und entwickeln Vertrauen in den Parcours. Mit zunehmendem Training können fortgeschrittene Parcours mit komplexeren Hindernisfolgen und höherem Tempo gemeistert werden."
      ],
      benefits: [
        "Perfekte Kombination aus Action, Geschick und Zusammenarbeit",
        "Stärkt die Fitness und Reaktionsfähigkeit des Hundes",
        "Fördert die Bindung im Team",
        "Großartiger Ausgleich zum Alltag",
        "Sportliche Herausforderung für Hund und Hundeführer",
        "Freude an der Bewegung",
        "Möglichkeit zur Wettkampfteilnahme"
      ],
      requirements: [
        "Grundgehorsam (Sitz, Platz, Bleib)",
        "Sozialverträglichkeit mit anderen Hunden",
        "Gesundheitliche Eignung (keine Gelenkprobleme)",
        "Sportliche Grundfitness des Halters"
      ],
      training: {
        duration: "90-120 Minuten pro Einheit",
        groupSize: "Maximal 6 Teams",
        level: "Anfänger bis Fortgeschrittene"
      },
      equipment: [
        "Hürden in verschiedenen Höhen",
        "Tunnel (offen und geschlossen)",
        "Slalomstangen",
        "A-Wand",
        "Wippe",
        "Steg",
        "Reifen",
        "Weitsprung"
      ],
      targetGroup: "Hunde und Halter, die Spaß an Bewegung haben und als Team neue Herausforderungen meistern möchten"
    },
    unterordnung: {
      title: "Unterordnung",
      subtitle: "Die Basis für eine starke Mensch-Hund-Beziehung",
      heroImage: "/images/sports/obedience/companion-dog.jpg",
      description: "Unterordnung ist ein zentraler Bestandteil des Hundetrainings und legt die Grundlage für eine harmonische Partnerschaft zwischen Mensch und Hund.",
      longDescription: [
        "Diese Disziplin geht weit über einfachen Gehorsam hinaus und betont das vertrauensvolle Zusammenspiel. Die Übungen in der Unterordnung schulen Aufmerksamkeit, Präzision und Kooperation und schaffen so eine starke Basis für viele andere Hundesportarten.",
        "In der Unterordnung lernt der Hund, aufmerksam und bereitwillig Kommandos seines Hundeführers zu befolgen. Dabei ist das Ziel nicht nur die präzise Ausführung, sondern auch die Freude an der gemeinsamen Arbeit. Unterordnung fördert den Gehorsam des Hundes und vertieft gleichzeitig das gegenseitige Vertrauen und die Bindung.",
        "Unterordnungstraining beginnt mit den Grundkommandos wie ‚Sitz', ‚Platz' und ‚Fuß'. Im Training achten wir auf eine ruhige, motivierende Atmosphäre und setzen auf positive Verstärkung, damit der Hund Spaß an den Übungen hat. Nach den Grundübungen kann das Team fortgeschrittenere Aufgaben bewältigen. Wer weiterkommen möchte, kann die Begleithundeprüfung (BHP) ablegen."
      ],
      benefits: [
        "Stärkung der Mensch-Hund-Bindung",
        "Verbesserter Grundgehorsam im Alltag",
        "Förderung von Aufmerksamkeit und Präzision",
        "Grundlage für weitere Hundesportarten",
        "Mentale Auslastung des Hundes",
        "Aufbau von gegenseitigem Vertrauen",
        "Harmonisches Zusammenleben"
      ],
      requirements: [
        "Impfnachweis",
        "Haftpflichtversicherung",
        "Bereitschaft zu regelmäßigem Training",
        "Geduld und Konsequenz"
      ],
      training: {
        duration: "60-90 Minuten pro Einheit",
        groupSize: "Maximal 8 Teams",
        level: "Welpen bis Fortgeschrittene"
      },
      equipment: [
        "Verschiedene Leinen und Halsbänder",
        "Motivationsspielzeug",
        "Futterdummy",
        "Pylonen für Slalom",
        "Apportierhölzer"
      ],
      targetGroup: "Alle Hunde ab 6 Monaten und ihre Halter, die eine vertrauensvolle Partnerschaft aufbauen möchten"
    },
    "rally-obedience": {
      title: "Rally Obedience",
      subtitle: "Teamarbeit und Spaß für Mensch und Hund",
      heroImage: "/images/sports/rally-obedience/rally-demo.jpg",
      description: "Rally Obedience ist eine Hundesportart, bei der der gemeinsame Spaß und die facettenreiche (gewaltfreie) Kommunikation im Vordergrund stehen.",
      longDescription: [
        "Es geht um eine enge Zusammenarbeit zwischen Mensch und Hund als Team. Diese Disziplin kombiniert Elemente aus dem Parcourslauf mit Unterordnungsübungen und fordert dabei sowohl Präzision als auch Geschwindigkeit. Dank ihrer abwechslungsreichen Aufgaben ist Rally Obedience für nahezu jeden Hundeführer und die meisten Hunde geeignet.",
        "In dieser Sportart ist die perfekte Kommunikation zwischen Hund und Hundeführer entscheidend. Die Kommunikation findet auf verschiedenen Ebenen statt: Sichtzeichen, Hörzeichen und Leckerlis sind erlaubt. Während des gesamten Parcours dürfen und sollen beide ständig miteinander interagieren.",
        "Der Parcours besteht aus mehreren Stationen, an denen Schilder die jeweilige Übung und die Laufrichtung vorgeben. Die Übungen umfassen klassische Unterordnungsaufgaben, Richtungsänderungen, Slalom und Kreise, Sprungübungen, Bleib- und Abrufübungen sowie Futterverweigerung."
      ],
      benefits: [
        "Stärkung der Bindung durch ständige Kommunikation",
        "Abwechslungsreiche und spannende Übungen",
        "Für jeden geeignet - jung oder alt, Anfänger oder Profi",
        "Gemeinsame geistige Auslastung",
        "Verbesserung der Kommunikation",
        "Spaß für beide Partner",
        "Hält mental und körperlich fit"
      ],
      requirements: [
        "Grundkenntnisse in Sitz, Platz, Steh",
        "Leinenführigkeit",
        "Freude an der Zusammenarbeit",
        "Motivation durch Futter oder Spielzeug"
      ],
      training: {
        duration: "90 Minuten pro Einheit",
        groupSize: "Maximal 6 Teams",
        level: "Anfänger bis Turnierreife"
      },
      equipment: [
        "Stationsschilder (über 60 verschiedene)",
        "Pylonen und Hütchen",
        "Sprünge in verschiedenen Höhen",
        "Wendemarken",
        "Motivationsmittel"
      ],
      targetGroup: "Für jeden geeignet, der nach einer Sportart sucht, die Freude bereitet und die Partnerschaft stärkt"
    },
    turnierhundesport: {
      title: "Turnierhundesport (THS)",
      subtitle: "Vielseitiger Wettkampfsport für sportliche Teams",
      heroImage: "/images/sports/ths/ths-training.jpg",
      description: "Der Turnierhundesport ist die Leichtathletik mit Hund - Vielseitigkeit, Schnelligkeit und Teamgeist sind gefragt!",
      longDescription: [
        "Der Turnierhundesport (THS) ist eine deutsche Hundesportart, die Elemente aus verschiedenen Disziplinen vereint. Im Mittelpunkt steht der Vierkampf, bestehend aus Gehorsamsübung, Hürdenlauf, Slalom und Hindernislauf.",
        "THS fördert die Fitness von Mensch und Hund gleichermaßen. Sie laufen gemeinsam, überwinden Hindernisse und meistern verschiedene Aufgaben. Die perfekte Sportart für alle, die sich gerne bewegen und Wettkämpfe lieben.",
        "Neben dem klassischen Vierkampf gibt es weitere Disziplinen wie den Geländelauf (2000m oder 5000m), Shorty (Kurzstrecke) und den Combinations-Speed-Cup (CSC). Für jeden ist etwas dabei!"
      ],
      benefits: [
        "Optimale Fitness für Mensch und Hund",
        "Vielseitige Herausforderungen",
        "Förderung des Teamgeists",
        "Wettkampfatmosphäre",
        "Messbare Leistungssteigerung",
        "Soziale Kontakte bei Turnieren",
        "Stolz auf gemeinsame Erfolge"
      ],
      requirements: [
        "Bestandene Begleithundeprüfung",
        "Gute Grundkondition",
        "Sportliche Ambitionen",
        "Regelmäßiges Training",
        "Gesunde Gelenke bei Hund und Halter"
      ],
      training: {
        duration: "120-180 Minuten pro Einheit",
        groupSize: "Maximal 8 Teams",
        level: "Mit BH-Prüfung"
      },
      equipment: [
        "Hürden (verschiedene Höhen)",
        "Slalomstangen",
        "Hindernisbahn",
        "Stoppuhr",
        "Startnummern",
        "Trainingshütchen"
      ],
      targetGroup: "Sportlich ambitionierte Teams mit Wettkampfambitionen"
    }
  };

  // Map 'obedience' to 'unterordnung' for backward compatibility
  const sportKey = sport === 'obedience' ? 'unterordnung' : sport;
  const content = sportKey ? sportContent[sportKey] : null;

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Sportart nicht gefunden</h1>
          <Link to="/trainings" className="btn-primary">
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO 
        title={content.title}
        description={content.description}
        url={`/sport/${sport}`}
        image={content.heroImage}
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Sportarten', url: '/trainings' },
          { name: content.title, url: `/sport/${sport}` }
        ]}
      />
      {/* Hero Section */}
      <div className="relative h-72 md:h-96 overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <img
          src={content.heroImage}
          alt={content.title}
          className="w-full h-full object-contain"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 max-w-5xl pb-6 md:pb-12">
            <Link to="/trainings" className="inline-flex items-center text-white mb-3 md:mb-4 hover:text-gray-200 transition-colors text-sm md:text-base">
              <ArrowLeftIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Zurück zur Übersicht
            </Link>
            <div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-1 md:mb-2">{content.title}</h1>
              <p className="text-lg md:text-2xl text-gray-200">{content.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-5xl py-8 md:py-12">
        {/* Notice for Agility */}
        {sportKey === 'agility' && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
            <p className="text-yellow-800 dark:text-yellow-200 text-center">
              <span className="font-semibold">Hinweis:</span> Agility-Training findet aktuell nicht statt.
              Bei Interesse kontaktieren Sie uns gerne über unser Kontaktformular.
            </p>
          </div>
        )}

        {/* Quick Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-12">
          <div className="card p-3 md:p-4 flex items-center gap-3 md:flex-col md:text-center md:gap-0">
            <ClockIcon className="w-8 h-8 md:mx-auto md:mb-2 text-primary-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm md:text-base">Dauer</p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{content.training.duration}</p>
            </div>
          </div>
          <div className="card p-3 md:p-4 flex items-center gap-3 md:flex-col md:text-center md:gap-0">
            <UserGroupIcon className="w-8 h-8 md:mx-auto md:mb-2 text-secondary-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm md:text-base">Gruppengröße</p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{content.training.groupSize}</p>
            </div>
          </div>
          <div className="card p-3 md:p-4 flex items-center gap-3 md:flex-col md:text-center md:gap-0">
            <TrophyIcon className="w-8 h-8 md:mx-auto md:mb-2 text-accent-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm md:text-base">Level</p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{content.training.level}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            {/* Description */}
            <div className="card p-5 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-neutral-900 dark:text-neutral-100">Über {content.title}</h2>
              <p className="text-base md:text-lg text-gray-700 mb-3 md:mb-4 leading-relaxed">{content.description}</p>
              {content.longDescription.map((paragraph, index) => (
                <p key={index} className="text-gray-600 mb-4 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Benefits */}
            <div className="card p-5 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">Ihre Vorteile</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {content.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <HeartIcon className="w-6 h-6 text-accent-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Equipment */}
            <div className="card p-5 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">Unsere Ausstattung</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {content.equipment.map((item, index) => (
                  <div key={index} className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 text-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 md:space-y-6">
            {/* Requirements */}
            <div className="card p-4 md:p-6 bg-blue-50 dark:bg-blue-900/20">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-200">
                Voraussetzungen
              </h3>
              <ul className="space-y-3">
                {content.requirements.map((req, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{req}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Target Group */}
            <div className="card p-4 md:p-6 bg-purple-50 dark:bg-purple-900/20">
              <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-200">
                Zielgruppe
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {content.targetGroup}
              </p>
            </div>

            {/* CTA */}
            <div className="card p-4 md:p-6 bg-primary-600 text-white">
              <h3 className="text-xl font-bold mb-4">Interesse geweckt?</h3>
              <p className="mb-6 text-white/90">
                Aktuell nur Warteliste – trag dich übers Kontaktformular ein.
              </p>
              <Link to="/kontakt" className="block w-full text-center bg-white text-accent-600 font-bold py-3 rounded-lg hover:bg-gray-100 transition-colors">
                Jetzt anmelden
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SportDetail;